import { Switch, Route, Redirect, useLocation } from "wouter";
import { Toaster } from "@/components/ui/toaster";
import NotFound from "@/pages/not-found";
import Layout from "@/components/layout/layout";
// Legacy routes (to be deprecated)
import FieldMappings from "@/pages/field-mappings";
import ChoiceMappings from "@/pages/choice-mappings";
import AutoDiscovery from "@/pages/auto-discovery";
import FieldSyncLogs from "@/pages/field-sync-logs";
import Tasks from "@/pages/tasks";
// New structured routes - Fields
import FieldMappingsPage from "@/pages/fields/mappings";
import FieldChoiceMappingsPage from "@/pages/fields/choice-mappings";
import FieldAutoDiscoveryPage from "@/pages/fields/auto-discovery";
import FieldSyncPage from "@/pages/fields/field-sync";
import FieldLogsPage from "@/pages/fields/logs";
// SharePoint routes
import SharePointDashboardPage from "@/pages/sharepoint/dashboard";
import SharePointJobsPage from "@/pages/sharepoint/jobs";
import NewSharePointJobPage from "@/pages/sharepoint/new";
import EditSharePointJobPage from "@/pages/sharepoint/edit";
import SharePointImagesPage from "@/pages/sharepoint/images";
// Title Folder Audit routes
import TitleFolderDashboardPage from "@/pages/title-folders/dashboard";
import TitleFoldersBrowsePage from "@/pages/title-folders/browse";
import OrphanFoldersPage from "@/pages/title-folders/orphans";
// System pages
import WelcomePage from "@/pages/welcome";
import AcceptInvitationPage from "@/pages/accept-invitation";
import ApiStatusPage from "@/pages/api-status";
import RateMonitor from "@/pages/ratemonitor";
import { FilterBuilderTest } from "@/pages/FilterBuilderTest";
import QueueManagement from "@/pages/queue-management";
import UserManagementPage from "@/pages/user-management";
import RolesPage from "@/pages/roles";
import ScheduledTasksPage from "@/pages/scheduled-tasks";
import CreateScheduledTaskPage from "@/pages/scheduled-tasks/create-scheduled-task";
import EditScheduledTaskPage from "@/pages/scheduled-tasks/[id]/edit";
import TaskExecutionsPage from "@/pages/task-executions";
import ADFMonitorPage from "@/pages/adf/monitor";
import AdfHealthPage from "@/pages/monitoring/adf-health";
import WatermarkConfigPage from "@/pages/adf/watermark-config";
import SqlSyncPage from "@/pages/sql-sync";
import OpSyncPage from "@/pages/op-sync";
import OpReadinessPage from "@/pages/op-readiness";
import SqlSyncPendingDataPage from "@/pages/sql-sync-pending";
import SqlSyncErrorsPage from "@/pages/sql-sync-errors";
import SqlSyncActivityPage from "@/pages/sql-sync-activity";
import SqlActivityPage from "@/pages/sql-activity";
import CacheManagementPage from "@/pages/cache-management";
import RoyaltyStatementsPage from "@/pages/royalty-statements";
import { RoyaltyStatementAccountPage } from "@/pages/royalty-statements";
import RoyaltyCcSettingsPage from "@/pages/royalty-cc-settings";
import RoyaltyOverduePage from "@/pages/royalty-overdue";
import RecordMergePage from "@/pages/record-merge";
import CopyrightPageGenerator from "@/pages/copyright-page";
import PrelimGeneratorPage from "@/pages/prelim/generator";
// Sentiment routes
import SentimentDashboardPage from "@/pages/sentiment/dashboard";
import SentimentConfigPage from "@/pages/sentiment/config";
// Dashboard (in-house BI reports)
import DashboardPage from "@/pages/dashboard";
import { ProtectedRoute } from "@/components/auth/protected-route";
import { PermissionProvider } from "@/contexts/PermissionContext";
import { useEffect, useState } from "react";
import { useAuthProvider } from "./hooks/use-azure-auth-provider";

function Router() {
  // Use the Azure auth provider
  const { isSignedIn, isLoaded } = useAuthProvider();
  const [location, setLocation] = useLocation();

  // Redirect based on authentication status
  useEffect(() => {
    if (isLoaded) {
      if (isSignedIn && (location === "/" || location === "/welcome")) {
        setTimeout(() => {
          console.log("Redirecting authenticated user to fields/mappings");
          setLocation("/fields/mappings");
        }, 100);
      } else if (!isSignedIn && location !== "/" && location !== "/welcome" && location !== "/invite") {
        setLocation("/welcome");
      }
    }
  }, [isLoaded, isSignedIn, location, setLocation]);

  return (
    <Switch>
      <Route path="/" component={WelcomePage} />
      <Route path="/welcome" component={WelcomePage} />
      <Route path="/invite" component={AcceptInvitationPage} />

      {/* New structured routes - Fields */}
      <Route path="/fields/mappings">
        <ProtectedRoute>
          <FieldMappingsPage />
        </ProtectedRoute>
      </Route>
      <Route path="/fields/choice-mappings">
        <ProtectedRoute>
          <FieldChoiceMappingsPage />
        </ProtectedRoute>
      </Route>
      <Route path="/fields/auto-discovery">
        <ProtectedRoute>
          <FieldAutoDiscoveryPage />
        </ProtectedRoute>
      </Route>
      <Route path="/fields/field-sync">
        <ProtectedRoute>
          <FieldSyncPage />
        </ProtectedRoute>
      </Route>
      <Route path="/fields/logs">
        <ProtectedRoute>
          <FieldLogsPage />
        </ProtectedRoute>
      </Route>

      {/* SharePoint routes */}
      <Route path="/sharepoint/dashboard">
        <ProtectedRoute>
          <SharePointDashboardPage />
        </ProtectedRoute>
      </Route>
      <Route path="/sharepoint/jobs/new">
        <ProtectedRoute>
          <NewSharePointJobPage />
        </ProtectedRoute>
      </Route>
      <Route path="/sharepoint/jobs/edit/:id">
        <ProtectedRoute>
          <EditSharePointJobPage />
        </ProtectedRoute>
      </Route>
      <Route path="/sharepoint/jobs">
        <ProtectedRoute>
          <SharePointJobsPage />
        </ProtectedRoute>
      </Route>
      <Route path="/sharepoint/images">
        <ProtectedRoute>
          <SharePointImagesPage />
        </ProtectedRoute>
      </Route>
      <Route path="/sharepoint">
        <ProtectedRoute>
          <Redirect to="/sharepoint/dashboard" />
        </ProtectedRoute>
      </Route>

      {/* Title Folder Audit routes */}
      <Route path="/title-folders/dashboard">
        <ProtectedRoute>
          <TitleFolderDashboardPage />
        </ProtectedRoute>
      </Route>
      <Route path="/title-folders/browse">
        <ProtectedRoute>
          <TitleFoldersBrowsePage />
        </ProtectedRoute>
      </Route>
      <Route path="/title-folders/orphans">
        <ProtectedRoute>
          <OrphanFoldersPage />
        </ProtectedRoute>
      </Route>
      <Route path="/title-folders">
        <ProtectedRoute>
          <Redirect to="/title-folders/dashboard" />
        </ProtectedRoute>
      </Route>

      {/* Legacy routes - redirect or keep for backward compatibility */}
      <Route path="/field-mappings">
        <ProtectedRoute>
          <FieldMappings />
        </ProtectedRoute>
      </Route>
      <Route path="/choice-mappings">
        <ProtectedRoute>
          <ChoiceMappings />
        </ProtectedRoute>
      </Route>
      <Route path="/auto-discovery">
        <ProtectedRoute>
          <AutoDiscovery />
        </ProtectedRoute>
      </Route>
      
      <Route path="/field-sync-logs">
        <ProtectedRoute>
          <FieldSyncLogs />
        </ProtectedRoute>
      </Route>
      
      <Route path="/tasks/:rest*">
        <ProtectedRoute>
          <Tasks />
        </ProtectedRoute>
      </Route>
      
      <Route path="/monitoring/rate">
        <ProtectedRoute>
          <RateMonitor />
        </ProtectedRoute>
      </Route>
      <Route path="/ratemonitor">
        <ProtectedRoute>
          <Redirect to="/monitoring/rate" />
        </ProtectedRoute>
      </Route>
      
      <Route path="/api-status">
        <ProtectedRoute>
          <ApiStatusPage />
        </ProtectedRoute>
      </Route>
      
      <Route path="/filter-builder-test">
        <ProtectedRoute>
          <FilterBuilderTest />
        </ProtectedRoute>
      </Route>

      <Route path="/monitoring/queues">
        <ProtectedRoute>
          <QueueManagement />
        </ProtectedRoute>
      </Route>
      <Route path="/queue-management">
        <ProtectedRoute>
          <Redirect to="/monitoring/queues" />
        </ProtectedRoute>
      </Route>

      <Route path="/security/users">
        <ProtectedRoute>
          <UserManagementPage />
        </ProtectedRoute>
      </Route>
      <Route path="/user-management">
        <ProtectedRoute>
          <Redirect to="/security/users" />
        </ProtectedRoute>
      </Route>
      <Route path="/security/roles">
        <ProtectedRoute>
          <RolesPage />
        </ProtectedRoute>
      </Route>
      <Route path="/roles">
        <ProtectedRoute>
          <Redirect to="/security/roles" />
        </ProtectedRoute>
      </Route>
      <Route path="/security">
        <ProtectedRoute>
          <Redirect to="/security/users" />
        </ProtectedRoute>
      </Route>

      <Route path="/scheduled-tasks/new">
        <ProtectedRoute>
          <CreateScheduledTaskPage />
        </ProtectedRoute>
      </Route>

      <Route path="/scheduled-tasks/:id/edit">
        <ProtectedRoute>
          <EditScheduledTaskPage />
        </ProtectedRoute>
      </Route>

      <Route path="/scheduled-tasks">
        <ProtectedRoute>
          <ScheduledTasksPage />
        </ProtectedRoute>
      </Route>

      <Route path="/task-executions">
        <ProtectedRoute>
          <TaskExecutionsPage />
        </ProtectedRoute>
      </Route>

      <Route path="/monitoring/adf">
        <ProtectedRoute>
          <ADFMonitorPage />
        </ProtectedRoute>
      </Route>
      <Route path="/monitoring/adf-health">
        <ProtectedRoute>
          <AdfHealthPage />
        </ProtectedRoute>
      </Route>
      <Route path="/adf/monitor">
        <ProtectedRoute>
          <Redirect to="/monitoring/adf" />
        </ProtectedRoute>
      </Route>
      <Route path="/adf/watermark-config">
        <ProtectedRoute>
          <WatermarkConfigPage />
        </ProtectedRoute>
      </Route>

      <Route path="/sql-sync">
        <ProtectedRoute>
          <SqlSyncPage />
        </ProtectedRoute>
      </Route>
      <Route path="/sql-sync/pending/:entityType">
        <ProtectedRoute>
          <SqlSyncPendingDataPage />
        </ProtectedRoute>
      </Route>
      <Route path="/sql-sync/errors">
        <ProtectedRoute>
          <SqlSyncErrorsPage />
        </ProtectedRoute>
      </Route>

      <Route path="/op-sync">
        <ProtectedRoute>
          <OpSyncPage />
        </ProtectedRoute>
      </Route>
      <Route path="/op-readiness">
        <ProtectedRoute>
          <OpReadinessPage />
        </ProtectedRoute>
      </Route>
      <Route path="/monitoring/activity-log">
        <ProtectedRoute>
          <SqlSyncActivityPage />
        </ProtectedRoute>
      </Route>
      <Route path="/sql-sync/activity">
        <ProtectedRoute>
          <Redirect to="/monitoring/activity-log" />
        </ProtectedRoute>
      </Route>

      <Route path="/sql-activity">
        <ProtectedRoute>
          <Redirect to="/monitoring/sql-activity" />
        </ProtectedRoute>
      </Route>

      <Route path="/monitoring/sql-activity">
        <ProtectedRoute>
          <SqlActivityPage />
        </ProtectedRoute>
      </Route>

      <Route path="/monitoring/cache">
        <ProtectedRoute>
          <CacheManagementPage />
        </ProtectedRoute>
      </Route>
      <Route path="/cache-management">
        <ProtectedRoute>
          <Redirect to="/monitoring/cache" />
        </ProtectedRoute>
      </Route>
      <Route path="/monitoring">
        <ProtectedRoute>
          <Redirect to="/monitoring/rate" />
        </ProtectedRoute>
      </Route>

      <Route path="/royalty-statements/:creditorId">
        {(params) => {
          const creditorId = parseInt(params.creditorId);
          if (isNaN(creditorId)) {
            return <ProtectedRoute><NotFound /></ProtectedRoute>;
          }
          return (
            <ProtectedRoute>
              <RoyaltyStatementAccountPage creditorId={creditorId} />
            </ProtectedRoute>
          );
        }}
      </Route>
      <Route path="/royalty-statements">
        <ProtectedRoute>
          <RoyaltyStatementsPage />
        </ProtectedRoute>
      </Route>
      <Route path="/royalty-cc-settings">
        <ProtectedRoute>
          <RoyaltyCcSettingsPage />
        </ProtectedRoute>
      </Route>
      <Route path="/royalty-overdue">
        <ProtectedRoute>
          <RoyaltyOverduePage />
        </ProtectedRoute>
      </Route>

      <Route path="/merge">
        <ProtectedRoute>
          <RecordMergePage />
        </ProtectedRoute>
      </Route>

      <Route path="/copyright-page">
        <ProtectedRoute>
          <CopyrightPageGenerator />
        </ProtectedRoute>
      </Route>

      <Route path="/prelim">
        <ProtectedRoute>
          <PrelimGeneratorPage />
        </ProtectedRoute>
      </Route>

      {/* Sentiment Analysis routes */}
      <Route path="/sentiment/dashboard">
        <ProtectedRoute>
          <SentimentDashboardPage />
        </ProtectedRoute>
      </Route>
      <Route path="/sentiment/config">
        <ProtectedRoute>
          <SentimentConfigPage />
        </ProtectedRoute>
      </Route>
      <Route path="/sentiment">
        <ProtectedRoute>
          <Redirect to="/sentiment/dashboard" />
        </ProtectedRoute>
      </Route>

      {/* Dashboard — in-house BI reports (Power BI replacement) */}
      <Route path="/dashboard/:reportId">
        <ProtectedRoute>
          <DashboardPage />
        </ProtectedRoute>
      </Route>
      <Route path="/dashboard">
        <ProtectedRoute>
          <DashboardPage />
        </ProtectedRoute>
      </Route>

      <Route component={NotFound} />
    </Switch>
  );
}

function AppContent() {
  const { isSignedIn, isLoaded } = useAuthProvider();
  const [location] = useLocation();

  // Log auth status for debugging
  useEffect(() => {
    if (isLoaded) {
      console.log('App initialized with authentication. User signed in:', isSignedIn);
    }
  }, [isLoaded, isSignedIn]);

  // Show loading state while auth is still being determined
  if (!isLoaded) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="flex flex-col items-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mb-4"></div>
          <p className="text-gray-500">Loading authentication state...</p>
        </div>
      </div>
    );
  }

  // Public routes that bypass PermissionProvider (for invitation acceptance)
  const isPublicRoute = location === '/invite' || location.startsWith('/invite?');

  if (isPublicRoute) {
    // Render invite page without PermissionProvider/Layout
    return <Router />;
  }

  // In production, only show layout for authenticated users
  return isSignedIn ? (
    <PermissionProvider>
      <Layout>
        <Router />
      </Layout>
    </PermissionProvider>
  ) : (
    <Router />
  );
}

function App() {
  const [mounted, setMounted] = useState(false);

  // Only mount once to avoid hydration issues
  useEffect(() => {
    setMounted(true);
    
    // Add Material Icons font loading detection
    document.body.classList.add('js-material-icons-pending');
    
    // Check if Material Icons font is available
    const checkFontLoaded = async () => {
      try {
        // Use Font Face Observer pattern
        const isFontLoaded = await new Promise((resolve) => {
          if (document.fonts) {
            // Modern browsers support
            document.fonts.ready.then(() => {
              // Create a test element with Material Icons
              const testElement = document.createElement('span');
              testElement.style.fontFamily = "'Material Icons'";
              testElement.style.fontSize = '24px';
              testElement.style.visibility = 'hidden';
              testElement.textContent = 'check';
              document.body.appendChild(testElement);
              
              // Check if the font loaded
              const fontLoaded = testElement.offsetWidth > 0;
              document.body.removeChild(testElement);
              resolve(fontLoaded);
            });
          } else {
            // Fallback for older browsers
            // Assume it's loaded after a timeout
            setTimeout(() => resolve(true), 500);
          }
        });
        
        // Update the body class based on font loading status
        document.body.classList.remove('js-material-icons-pending');
        if (isFontLoaded) {
          document.body.classList.add('js-material-icons-loaded');
        } else {
          document.body.classList.add('js-material-icons-failed');
          console.warn('Material Icons font failed to load');
        }
      } catch (error) {
        document.body.classList.remove('js-material-icons-pending');
        document.body.classList.add('js-material-icons-failed');
        console.error('Error checking Material Icons font:', error);
      }
    };
    
    checkFontLoaded();
  }, []);
  
  if (!mounted) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="flex flex-col items-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mb-4"></div>
          <p className="text-gray-500">Initializing application...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <AppContent />
      <Toaster />
    </>
  );
}

export default App;
